import React, { useEffect, useState } from 'react';

import { digitsEnToFa, digitsFaToEn } from '@persian-tools/persian-tools';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';

import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import jMoment from 'moment-jalaali';
import NewDialog from 'components/NewDialog';
import NewDialogTitle from 'components/NewDialogTitle';
import NewDialogContent from 'components/NewDialogContent';
import NewDialogActions from 'components/NewDialogActions';
import { FormControl, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';

function helper(date, format) {
  return Number(digitsFaToEn(jMoment(date).format(format)));
}

export default function Date(props) {
  const { schema, formData, onChange, disabled, readonly, rawErrors, required } = props;

  const closeOnSelect = true;
  const [open, setOpen] = useState(false);

  const init = digitsFaToEn(jMoment().format('YYYY-MM-DD HH:mm'));
  const [date, setDate] = useState(formData || init);
  const [selectedValue, setSelectedValue] = useState(formData || init);

  const [hour, setHour] = useState(helper(date, 'HH'));
  const [minute, setMinute] = useState(helper(date, 'mm'));

  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);

  const goToday = () => {
    setSelectedValue(init);
    setHour(helper(undefined, 'HH'));
    setMinute(helper(undefined, 'mm'));
    closeOnSelect && onClose();
  };

  const onChangeHour = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && (value === '' || (Number(value) >= 0 && Number(value) <= 23))) {
      setHour(value);
    }
  };

  const onChangeMinute = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && (value === '' || (Number(value) >= 0 && Number(value) <= 59))) {
      setMinute(value);
    }
  };

  const onSelect = ({ closeOnSelect = true }) => {
    setSelectedValue(digitsFaToEn(jMoment(`${hour}:${minute}`, 'HH:mm').format('YYYY-MM-DD HH:mm')));
    onChange(digitsFaToEn(jMoment(`${hour}:${minute}`, 'HH:mm').format('YYYY-MM-DD HH:mm')));
    closeOnSelect && onClose();
  };

  const onPrevHour = () => {
    if (hour === 0) {
      setHour(23);
      return;
    }
    return setHour((prev) => +prev - 1);
  };

  const onNextHour = () => {
    if (hour === 23) {
      setHour(0);
      return;
    }
    return setHour((prev) => +prev + 1);
  };

  const onPrevMinute = () => {
    if (minute === 0) {
      setMinute(59);
      return onPrevHour();
    }
    return setMinute((prev) => +prev - 1);
  };

  const onNextMinute = () => {
    if (minute === 59) {
      setMinute(0);
      return onNextHour();
    }
    return setMinute((prev) => +prev + 1);
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timer = setTimeout(() => {
      if (loading && formData) {
        setDate(formData || init);
        setSelectedValue(formData || init);
        setHour(helper(formData || init, 'HH'));
        setMinute(helper(formData || init, 'mm'));
      }
      return setLoading(false);
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [formData]);

  useEffect(() => {
    if (hour && minute) {
      return onSelect({ closeOnSelect: false });
    }
  }, [hour, minute]);

  const renderClockFace = () => {
    const hourDegrees = (hour % 12) * 30 + (minute / 60) * 30 + 90; // 360° / 12 hours = 30° per hour
    const minuteDegrees = minute * 6 + 90; // 360° / 60 minutes = 6° per minute

    return (
      <Box
        sx={{
          width: '200px',
          height: '200px',
          display: 'flex',
          borderRadius: '50%',
          border: '4px solid',
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'center',
          borderColor: 'action.selected',
          bgcolor: 'Background.paper',
          boxShadow: (theme) => theme.shadows[10],
        }}
      >
        {/* عقربه ساعت */}
        <Box
          sx={{
            width: '60px',
            height: '4px',
            position: 'absolute',
            translate: '-30px 0',
            bgcolor: 'primary.main',
            transformOrigin: '100% 50%',
            transform: `rotate(${hourDegrees}deg)`,
          }}
        />
        {/* عقربه دقیقه */}
        <Box
          sx={{
            width: '80px',
            height: '2px',
            position: 'absolute',
            translate: '-40px 0',
            bgcolor: 'primary.dark',
            transformOrigin: '100% 50%',
            transform: `rotate(${minuteDegrees}deg)`,
          }}
        />
        {/* مرکز ساعت */}
        <Box
          sx={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            position: 'absolute',
            bgcolor: 'primary.darker',
          }}
        />
        <Box
          sx={{
            top: '20%',
            position: 'absolute',
          }}
        >
          <Typography color="textDisabled" variant="subtitle1" fontSize={10}>
            RAHKO
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          borderColor: rawErrors && rawErrors.length > 0 ? 'error' : 'action.disabledBackground',
        }}
      >
        <CardActionArea
          onClick={onOpen}
          disabled={disabled || readonly}
          focusRipple={rawErrors && rawErrors.length > 0}
        >
          <Stack px={1} height={56} columnGap={0.5} direction="row" alignItems="center" alignContent="center">
            <Typography flexGrow={1} fontSize={12} align="right" variant="body1" color="textSecondary">
              {schema.title}
            </Typography>
            <Chip
              sx={{ width: 64, fontSize: 10 }}
              size="small"
              disabled={disabled}
              label={jMoment(selectedValue).format('A')}
            />
            <Typography variant="subtitle2" sx={{ mx: 1 }}>
              {formData ? digitsEnToFa(jMoment(selectedValue).format('HH:mm')) : 'انتخاب'}
            </Typography>
            <AccessTimeRoundedIcon color="disabled" fontSize="small" />
          </Stack>
        </CardActionArea>
      </Card>
      <NewDialog label="ساعت" open={open} onClose={onClose}>
        <NewDialogTitle onClose={onClose} title={schema.title} />
        <NewDialogContent isPopup>
          <Stack p={1} flex={1} rowGap={1} alignItems="center" bgcolor="background.paper">
            <Box py={4}>{renderClockFace()}</Box>
            <Stack columnGap={1} direction="row-reverse" alignItems="center" justifyContent="center">
              <Stack>
                <Button size="small" onClick={onNextHour}>
                  <ArrowDropUpRoundedIcon />
                </Button>
                <Button size="small" onClick={onPrevHour}>
                  <ArrowDropDownRoundedIcon />
                </Button>
              </Stack>
              <TextField
                sx={{ width: 80 }}
                type="number"
                label="ساعت"
                value={Number(hour)}
                onChange={onChangeHour}
                inputProps={{
                  min: 0,
                  max: 23,
                  step: 1,
                  style: {
                    textAlign: 'center',
                    fontSize: 18,
                    fontWeight: 'bold',
                  },
                }}
              />
              :
              <TextField
                sx={{ width: 80 }}
                type="number"
                label="دقیقه"
                value={Number(minute)}
                onChange={onChangeMinute}
                inputProps={{
                  min: 0,
                  max: 59,
                  step: 1,
                  style: {
                    textAlign: 'center',
                    fontSize: 18,
                    fontWeight: 'bold',
                  },
                }}
              />
              <Stack>
                <Button size="small" onClick={onNextMinute}>
                  <ArrowDropUpRoundedIcon />
                </Button>
                <Button size="small" onClick={onPrevMinute}>
                  <ArrowDropDownRoundedIcon />
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </NewDialogContent>
        <NewDialogActions>
          <Stack width="100%" alignItems="center" direction="row-reverse" justifyContent="space-between">
            <Button size="medium" color="primary" variant="contained" onClick={onSelect}>
              انتخاب
            </Button>
            {jMoment(selectedValue).format('jYYYY-jMM-jDD') !== jMoment().format('jYYYY-jMM-jDD') && (
              <Button
                endIcon={<AccessTimeRoundedIcon />}
                size="medium"
                color="primary"
                variant="outlined"
                onClick={goToday}
              >
                همین الان
              </Button>
            )}
          </Stack>
        </NewDialogActions>
      </NewDialog>
    </>
  );
}
